import { KATON_MEET, VIRTUAL } from '../../utils/constants';

// Which service runs live classes:
// - virtual schools: REACT_APP_VIRTUAL_MEET_SERVICE, or REACT_APP_MEET_SERVICE when it is not set
// - other schools: REACT_APP_MEET_SERVICE
// Workshops and PTA meetings always use the Chime/Jitsi flow.
export const getLiveClassMeetService = (schoolType?: string | null) => {
  const service = schoolType === VIRTUAL
    ? process.env.REACT_APP_VIRTUAL_MEET_SERVICE?.trim() || process.env.REACT_APP_MEET_SERVICE
    : process.env.REACT_APP_MEET_SERVICE;
  return service?.trim().toUpperCase();
};

export const isKatonMeetLiveClass = (schoolType?: string | null) => getLiveClassMeetService(schoolType) === KATON_MEET;

// Base URL of Katon Meet (REACT_APP_KATON_MEET_URL), without a trailing slash
export const KATON_MEET_URL = (process.env.REACT_APP_KATON_MEET_URL || '').trim().replace(/\/+$/, '');

export const KATON_MEET_ORIGIN = (() => {
  try {
    return KATON_MEET_URL ? new URL(KATON_MEET_URL).origin : '';
  } catch {
    return '';
  }
})();

// Messages exchanged with the Katon Meet iframe (see docs/KATON_MEET_IFRAME_FINAL_PLAN.md)
export const KATON_MEET_EVENTS = {
  // Login handover: Meet sends READY, Classroom answers with AUTH (tokens + mId).
  // Katon Meet refreshes the token itself afterwards, so there is no token-refresh event.
  READY: 'KM_READY',
  AUTH: 'KM_AUTH',
  SET_PIP: 'KM_SET_PIP',
  LEFT: 'KM_LEFT',
  ENDED: 'KM_ENDED',
  CLOSE: 'KM_CLOSE',
  MOBILE_SCREEN: 'KM_MOBILE_SCREEN',
  HOST_KICKED: 'KM_HOST_KICKED',
} as const;

// Permissions delegated to the Katon Meet iframe
export const KATON_MEET_IFRAME_ALLOW = [
  'camera',
  'microphone',
  'display-capture',
  'fullscreen',
  'autoplay',
  'clipboard-write',
  'speaker-selection',
].join('; ');

// The session's Katon Meet meeting id, from getSession's katonMeetResponse
// ({ mId, mStartTime, mEndTime, mTitle, mCode, mmId, … }). The response may arrive as a JSON object or a JSON string.
export const getKatonMeetMeetingId = (katonMeetResponse: unknown): string | undefined => {
  try {
    const response = typeof katonMeetResponse === 'string' ? JSON.parse(katonMeetResponse) : katonMeetResponse;
    const mId = (response as { mId?: unknown } | null)?.mId;
    return typeof mId === 'string' && mId ? mId : undefined;
  } catch {
    return undefined;
  }
};

// Live class recordings: Katon Meet recordings are served from Katon Meet's base URL, all others
// (isKatonMeet false, null or missing) from Classroom's own URL, as before
export const buildRecordingUrl = (savedSessionUrl?: string | null, isKatonMeet?: boolean | null) => {
  if (!savedSessionUrl) return '';
  const baseUrl = isKatonMeet ? `${KATON_MEET_URL}/` : process.env.REACT_APP_FRONTEND_URL || '';
  return `${baseUrl}${savedSessionUrl}`;
};

// The iframe opens the meeting's lobby directly (Katon Meet's /meeting/<mId>/prejoin),
// or Katon Meet's base URL when the meeting id isn't known
export const buildKatonMeetLobbyUrl = (meetingId?: string | null) =>
  meetingId ? `${KATON_MEET_URL}/meeting/${encodeURIComponent(meetingId)}/prejoin` : `${KATON_MEET_URL}/`;
